import * as _ from "lodash";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { GfxUtils, NodeType } from "../../../common/gfxUtils";
import { UiUtils } from "../../../common/uiUtils";
import { GeometryTools } from "../../../common/geometryTools";
import { ColorConstants } from "../../../common/constants";
import { viewerOptions } from "../../../viewer";
import { StepVisualizer } from "./stepVisualizer";

import { BalconyGroup, BalconyInfo, BalconyPanel } from "./balconyInfo";

enum CreateStep {
    None,
    UnderSideSelection,
    HeightSelection,
    PanelSelection
}

interface CreateProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
    addBalcony: (apartment: string, level: number, line: string, panels: BalconyPanel[], drawPanels: boolean, meas: any) => void;
    removePanels: (panels: number[]) => void;
    closeWizard: () => void;
    originalModelId: number;
    panelsModelId: number;
    apartment: string;
    level: number;
    line: string;
    gfxUtils: GfxUtils;
}

export function CreateSteps(props: CreateProps) {
    const stepRef = useRef<CreateStep>();
    const modelRef = useRef<number>();

    const panelsRef = useRef<BalconyPanel[]>();

    const selectedPanelsRef = useRef<number[]>();

    const [selectionOn, setSelectionOn] = useState(true);
    const [selectionModel, setSelectionModel] = useState(props.originalModelId);
    const [underSideObject, setUnderSideObject] = useState<number>(null);
    const [upperSideObject, setUpperSideObject] = useState<number>(null);
    const [selectedPanels, setSelectedPanels] = useState<number[]>([]);
    const [createStep, setCreateStep] = useState(CreateStep.UnderSideSelection);
    const [selectedId, setSelectedId] = useState<number>(null);
    const [helpText, setHelpText] = useState("");

    stepRef.current = createStep;
    modelRef.current = selectionModel;

    function updateObject(dbIds: number[]) {
        switch (stepRef.current) {
            case CreateStep.UnderSideSelection:
                setUnderSideObject(dbIds[0]);
                break;
            case CreateStep.HeightSelection:
                setUpperSideObject(dbIds[0]);
                break;
            case CreateStep.PanelSelection:
                setSelectedPanels([...dbIds]);
                break;
            default:
        }

        setSelectedId(dbIds[0]);
    }

    function selectionListener({ selections }) {
        if (selectionModel !== null) {
            const nodeSel = _.find(selections, sel => sel.model.id === modelRef.current); // TODO: how to select primary model
            if (nodeSel != null) {
                if (stepRef.current != CreateStep.PanelSelection) {
                    if (nodeSel.dbIdArray.length > 1) {
                        UiUtils.notifyWarning("Select only one object");
                        return;
                    }
                }

                if (stepRef.current === CreateStep.PanelSelection) {
                    if (!selectedPanelsRef.current || selectedPanelsRef.current.length == 0 ||
                        selectedPanelsRef.current.length > nodeSel.dbIdArray.length)
                        selectedPanelsRef.current = nodeSel.dbIdArray;
                    else {
                        let diff = _.difference(nodeSel.dbIdArray, selectedPanelsRef.current);
                        if (diff.length > 0)
                            selectedPanelsRef.current.push(...diff);
                    }

                    updateObject(selectedPanelsRef.current);
                }
                else
                    updateObject(nodeSel.dbIdArray);
            }
        }
    }

    const objectBbox = (id: number, modelId: number) => {
        let model = props.viewer.getAllModels()[modelId];
        let fragList = model.getFragmentList();
        let instanceTree = model.getInstanceTree();
        let fragIds: number[] = [];
        instanceTree.enumNodeFragments(id, (fragId) => { fragIds.push(fragId); }, true);
        let box = props.gfxUtils.getModifiedWorldBoundingBox(fragIds, fragList);
        return box;
    }

    const addPanels = async (underSideObject: number, upperSideObject: number): Promise<BalconyPanel[]> => {
        props.viewer.clearSelection();

        let fragIds: number[] = [];
        let model = props.viewer.getVisibleModels()[0];
        let instanceTree = model.getInstanceTree();

        let fragList = model.getFragmentList();

        let maxZ = model.getBoundingBox().min.z;
        let maxZfragId: number;

        // Underside (width, direction)
        instanceTree.enumNodeFragments(underSideObject, (fragId) => { fragIds.push(fragId); }, true);

        // Take fragment with max z-coordinate
        fragIds.forEach(async (fragId) => {
            let box = props.gfxUtils.getModifiedWorldBoundingBox([fragId], fragList);
            if (box.max.z > maxZ) {
                maxZ = box.max.z;
                maxZfragId = fragId;
            }
        });

        var renderProxy = props.viewer.impl.getRenderProxy(model, maxZfragId);

        let triangles = GeometryTools.getFragmentTriangles(renderProxy, new THREE.Vector3(0, 0, 1));
        triangles.mergeVertices();

        let vertices = GeometryTools.sortNonIntersecting(triangles.vertices);

        vertices.push(vertices[0]);

        // Upperside (height)
        fragIds = [];
        instanceTree.enumNodeFragments(upperSideObject, (fragId) => { fragIds.push(fragId); }, true);

        let minZ = model.getBoundingBox().max.z;
        let minZfragId: number;

        // Take fragment with min z-coordinate
        fragIds.forEach(async (fragId) => {
            let box = props.gfxUtils.getModifiedWorldBoundingBox([fragId], fragList);
            if (box.min.z < minZ) {
                minZ = box.min.z;
                minZfragId = fragId;
            }
        });

        renderProxy = props.viewer.impl.getRenderProxy(model, minZfragId);

        let upperTriangles = GeometryTools.getFragmentTriangles(renderProxy, new THREE.Vector3(0, 0, -1));
        let height = upperTriangles.vertices[0].z - triangles.vertices[0].z;

        let modelNumber = null;
        let panels: BalconyPanel[] = [];

        let maxDbId = 0;
        if (props.panelsModelId != null) {
            let panelsModel = props.viewer.getAllModels().find(m => m.id === props.panelsModelId);
            let ids = _.keys(panelsModel.getInstanceTree().nodeAccess.dbIdToIndex).map(i => Number(i));
            let max = Math.max(...ids);
            if (max > 0)
                maxDbId = max;
        }

        let panelColor = new THREE.Color(ColorConstants.NAMES.mediumvioletred);
        for (var i = 0; i < vertices.length - 1; i++) {
            maxDbId++;
            modelNumber = await props.gfxUtils.drawPlaneNode(maxDbId, vertices[i], vertices[i + 1],
                new THREE.Line3(vertices[i], vertices[i + 1]).center().add(new THREE.Vector3(0, 0, height)),
                panelColor);
            if (modelNumber < 0)
                break;

            let panel = new BalconyPanel(maxDbId, vertices[i].toModelUnits(), vertices[i + 1].toModelUnits(),
                height * viewerOptions.unitData.viewerToModelScale, panelColor, props.panelsModelId);

            panels.push(panel);
        }

        return panels;
    }

    useEffect(() => {
        props.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT,
            selectionListener);

        return () => props.viewer.removeEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT,
            selectionListener);
    }, []);

    useEffect(() => {
        switch (createStep) {
            case CreateStep.UnderSideSelection:
                props.viewer.clearSelection();
                setHelpText("Select one balcony slab object");
                setSelectionModel(props.originalModelId);
                break;
            case CreateStep.HeightSelection:
                setHelpText("Select the height bounding object");
                setSelectionModel(props.originalModelId);
                break;
            case CreateStep.PanelSelection:
                setHelpText("Choose the necessary panels and create balcony");
                setSelectionModel(props.panelsModelId);
                break;
            default:
                setHelpText("This is an area for help texts");
        }
    }, [createStep]);

    return (
        <div id="steps-react" className="react-content m-1">
            <div>
                <div className="row pt-1 pb-2">
                    <div className="col d-flex justify-content-center">
                        <StepVisualizer steps={["Slab", "Height", "Panels"]} currentStep={createStep} />
                    </div>
                </div>
            </div>

            <div className="row d-flex align-items-center justify-content-center mt-1 mb-2 border border-white" style={{ height: "50px" }}>
                <span style={{ fontSize: "medium" }}>{helpText}</span>
            </div>

            <div className="row py-1">
                <div className="col d-flex justify-content-center">
                    {"Object id: " + (selectedId ?? "")}
                </div>
            </div>

            <div className="row align-items-center justify-content-between py-1">
                <div className="col">
                    <button id="cancel" className="btn btn-secondary"
                        onClick={(e) => {
                            if (panelsRef.current && panelsRef.current.length > 0)
                                props.removePanels(panelsRef.current.map(i => i.id));
                            props.closeWizard();
                        }}>
                        Cancel
                    </button>
                </div>
                <div className="col">
                    <button id="stop1" className="btn btn-primary float-right"
                        disabled={selectedId === null}
                        onClick={async (e) => {
                            setSelectedId(null);
                            setCreateStep((prev) => prev + 1);
                            switch (createStep) {
                                case CreateStep.HeightSelection:
                                    // TODO: Check z-coordinates
                                    let panels = await addPanels(underSideObject, upperSideObject);
                                    if (panels.length > 0) {
                                        panelsRef.current = panels;
                                    }
                                    break;
                                case CreateStep.PanelSelection:
                                    props.viewer.clearSelection();
                                    let notSelected = panelsRef.current.filter(i => !selectedPanels.includes(i.id));
                                    props.removePanels(notSelected.map(i => i.id));
                                
                                    let tempPanels: BalconyPanel[] = [];
                                    for (const id of selectedPanels) {
                                        tempPanels.push(panelsRef.current.find(i => i.id === id));
                                    }
                                    props.addBalcony(props.apartment, props.level, props.line, tempPanels, false, null);
                                    setCreateStep(CreateStep.None);

                                    break;
                                default:
                            }
                        }}>
                        {createStep < CreateStep.PanelSelection ? "Next" : "Add"}
                    </button>
                </div>
            </div>
        </div>
    );
}